import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Lightbulb,
  Bookmark,
  BookmarkCheck,
  ArrowRight,
  Loader2,
  Wand2,
  TrendingUp,
  Calendar,
  Hash,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const INDUSTRIES = [
  'Technology', 'E-commerce', 'Healthcare', 'Finance', 'Education',
  'Food & Beverage', 'Travel', 'Fashion', 'Fitness', 'Real Estate',
  'Entertainment', 'Marketing', 'Non-profit', 'Other'
];

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', emoji: '📘' },
  { id: 'instagram', name: 'Instagram', emoji: '📸' },
  { id: 'twitter', name: 'Twitter/X', emoji: '🐦' },
  { id: 'linkedin', name: 'LinkedIn', emoji: '💼' },
  { id: 'tiktok', name: 'TikTok', emoji: '🎵' }
];

const CATEGORY_STYLES = {
  trend: { bg: 'bg-purple-100', text: 'text-purple-700', icon: TrendingUp },
  holiday: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Calendar },
  evergreen: { bg: 'bg-green-100', text: 'text-green-700', icon: RefreshCw },
  engagement: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Lightbulb }
};

const ContentIdeasPage = () => {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState('');

  // Generate form state
  const [topic, setTopic] = useState('');
  const [industry, setIndustry] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ideasRes, calendarsRes] = await Promise.all([
        api.get('/ai/ideas'),
        api.get('/calendars')
      ]);
      setIdeas(ideasRes.data);
      setCalendars(calendarsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/ai/generate-ideas', {
        topic: topic || null,
        industry: industry || null,
        platforms: selectedPlatforms.length > 0 ? selectedPlatforms : null
      });
      setIdeas([...response.data.ideas, ...ideas]);
      toast.success('Ideas generated successfully!');
      setTopic('');
    } catch (error) {
      if (!isUpgradeError(error)) {
        toast.error('Failed to generate ideas. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleSave = async (ideaId) => {
    try {
      const response = await api.post(`/ai/ideas/${ideaId}/save`);
      setIdeas(ideas.map(idea => 
        idea.idea_id === ideaId ? { ...idea, saved: response.data.saved } : idea
      ));
      toast.success(response.data.saved ? 'Idea saved!' : 'Idea unsaved');
    } catch (error) {
      toast.error('Failed to save idea');
    }
  };

  const handleConvertToPost = async () => {
    if (!selectedIdea || !selectedCalendar) return;
    try {
      const response = await api.post(`/ai/ideas/${selectedIdea.idea_id}/convert`, null, {
        params: { calendar_id: selectedCalendar }
      });
      toast.success('Idea converted to post!');
      setShowConvertDialog(false);
      navigate(`/posts/${response.data.post_id}/edit`);
    } catch (error) {
      toast.error('Failed to convert idea');
    }
  };

  const filteredIdeas = activeTab === 'saved'
    ? ideas.filter(i => i.saved)
    : ideas;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="content-ideas-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-500" />
              Content Ideas
            </h1>
            <p className="text-slate-600 mt-1">AI-powered inspiration for your social media</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Generator Panel */}
          <Card className="border-slate-100 lg:sticky lg:top-6 h-fit">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-emerald-500" />
                Generate Ideas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Topic (optional)</Label>
                <Input
                  placeholder="e.g., product launch, holiday sale..."
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  data-testid="topic-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Industry</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind} value={ind.toLowerCase()}>
                        {ind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Platforms</Label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((platform) => {
                    const isSelected = selectedPlatforms.includes(platform.id);
                    return (
                      <button
                        key={platform.id}
                        type="button"
                        onClick={() => {
                          setSelectedPlatforms(prev =>
                            isSelected
                              ? prev.filter(p => p !== platform.id)
                              : [...prev, platform.id]
                          );
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                          isSelected
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        } border`}
                      >
                        <span>{platform.emoji}</span>
                        <span>{platform.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="generate-ideas-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate 5 Ideas
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Powered by AI • Fresh ideas every time
              </p>
            </CardContent>
          </Card>

          {/* Ideas List */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList>
                <TabsTrigger value="all">
                  All Ideas ({ideas.length})
                </TabsTrigger>
                <TabsTrigger value="saved">
                  <Bookmark className="h-4 w-4 mr-1" />
                  Saved ({ideas.filter(i => i.saved).length})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {filteredIdeas.length === 0 ? (
              <Card className="border-slate-100">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Lightbulb className="h-16 w-16 text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {activeTab === 'saved' ? 'No saved ideas yet' : 'No ideas yet'}
                  </h3>
                  <p className="text-slate-500 text-center max-w-sm mb-6">
                    {activeTab === 'saved'
                      ? 'Save ideas you like to access them quickly later'
                      : 'Generate some AI-powered content ideas to get started'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredIdeas.map((idea, i) => {
                  const categoryStyle = CATEGORY_STYLES[idea.category] || CATEGORY_STYLES.evergreen;
                  const CategoryIcon = categoryStyle.icon;

                  return (
                    <motion.div
                      key={idea.idea_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Card className="border-slate-100 hover:border-emerald-200 transition-colors">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex items-center gap-2">
                              <Badge className={`${categoryStyle.bg} ${categoryStyle.text}`}>
                                <CategoryIcon className="h-3 w-3 mr-1" />
                                {idea.category}
                              </Badge>
                              {idea.platforms?.length > 0 && (
                                <span className="text-sm">
                                  {idea.platforms.map(p => 
                                    PLATFORMS.find(pl => pl.id === p)?.emoji || '📱'
                                  ).join(' ')}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleToggleSave(idea.idea_id)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                idea.saved
                                  ? 'text-amber-500 bg-amber-50 hover:bg-amber-100'
                                  : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'
                              }`}
                            >
                              {idea.saved ? (
                                <BookmarkCheck className="h-5 w-5" />
                              ) : (
                                <Bookmark className="h-5 w-5" />
                              )}
                            </button>
                          </div>

                          <h3 className="font-semibold text-slate-900 mb-2">{idea.title}</h3>
                          <p className="text-slate-600 text-sm mb-4 whitespace-pre-wrap">
                            {idea.content}
                          </p>

                          {idea.hashtags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-4">
                              {idea.hashtags.map((tag, j) => (
                                <Badge key={j} variant="outline" className="text-xs">
                                  <Hash className="h-3 w-3 mr-0.5" />
                                  {tag.replace('#', '')}
                                </Badge>
                              ))}
                            </div>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedIdea(idea);
                              setShowConvertDialog(true);
                            }}
                            className="w-full border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                          >
                            Convert to Post
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Convert to Post Dialog */}
        <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convert to Post</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label className="mb-2 block">Select Calendar</Label>
              <Select value={selectedCalendar} onValueChange={setSelectedCalendar}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a calendar" />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.calendar_id} value={cal.calendar_id}>
                      {cal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {calendars.length === 0 && (
                <p className="text-sm text-slate-500 mt-2">
                  You need to create a calendar first.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConvertToPost}
                disabled={!selectedCalendar}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Create Post
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ContentIdeasPage;
